<!-- Hackathon leaderboard: tabular view with all metrics -->
<ng-container *ngIf="boardVisualVariant === 'hackathon'">
  <section class="leaderboard-panel">
    <header class="leaderboard-panel__header">
      <div class="leaderboard-panel__title-group">
        <div class="leaderboard-panel__title-row">
          <h2 class="leaderboard-panel__title">{{ headingLabel }}</h2>
          <div class="leaderboard-metrics-hint">
            <button
              type="button"
              class="leaderboard-metrics-hint__trigger"
              aria-label="Why do metrics differ between datasets?"
              [attr.aria-expanded]="showMetricsLegend"
              (click)="toggleMetricsLegend($event)"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                <path d="M12 16v-5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                <circle cx="12" cy="8" r="1" fill="currentColor"/>
              </svg>
            </button>

            <ng-container *ngIf="showMetricsLegend">
              <div class="leaderboard-metrics-hint__backdrop" (click)="closeMetricsLegend()"></div>
              <div class="leaderboard-metrics-hint__panel" role="dialog" aria-label="Metric definitions" (click)="$event.stopPropagation()">
                <div class="leaderboard-metrics-hint__panel-header">
                  <span>{{ activeMetricLegend?.title || 'Why metrics vary by dataset' }}</span>
                  <button type="button" class="leaderboard-metrics-hint__close" aria-label="Close" (click)="closeMetricsLegend()">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                  </button>
                </div>
                <p class="leaderboard-metrics-hint__intro">
                  Every dataset reports a primary pair (used for ranking), a secondary efficiency metric, and a set of
                  available metrics specific to its clinical task — which is why the column count differs by condition.
                </p>
                <ul *ngIf="activeMetricLegend as legend; else noLegendTemplate" class="leaderboard-metrics-hint__list">
                  <li *ngFor="let metric of legend.metrics" class="leaderboard-metrics-hint__item">
                    <div class="leaderboard-metrics-hint__item-head">
                      <span class="leaderboard-metrics-hint__item-label">{{ metric.label }}</span>
                      <span
                        class="leaderboard-metrics-hint__badge"
                        [ngClass]="'leaderboard-metrics-hint__badge--' + metric.role"
                      >{{ metric.role }}</span>
                    </div>
                    <p class="leaderboard-metrics-hint__item-desc">{{ metric.description }}</p>
                  </li>
                </ul>
                <ng-template #noLegendTemplate>
                  <p class="leaderboard-metrics-hint__intro">
                    Select a dataset to see its metric definitions.
                  </p>
                </ng-template>
              </div>
            </ng-container>
          </div>
        </div>
        <p class="leaderboard-panel__subtitle" *ngIf="hackathonTrackLabel">{{ hackathonTrackLabel }}</p>
      </div>

      <div class="leaderboard-panel__toolbar">
        <div class="leaderboard-dataset-select-wrapper">
          <label class="leaderboard-dataset-select-label" for="leaderboardDatasetSelect">Dataset</label>
          <select
            id="leaderboardDatasetSelect"
            class="leaderboard-dataset-select"
            aria-label="Benchmarking datasets"
            [value]="activeHackathonTrack"
            (change)="onHackathonDatasetSelect($event)"
          >
            <option *ngFor="let tab of hackathonTabs" [value]="tab.key">{{ tab.label }}</option>
          </select>
        </div>
      </div>
    </header>

    <div
      class="leaderboard-table-card"
      *ngIf="sortedTableRows.length > 0 || isHackathonLoading; else noResultsTemplate"
    >
      <ng-template #rankCell let-row let-rowIndex="rowIndex">
        <div class="leaderboard-cell leaderboard-cell--center">
          <span class="leaderboard-rank" [ngClass]="getRankClass(row['rank'])">
            {{ getDisplayRank(row, rowIndex) }}
          </span>
        </div>
      </ng-template>

      <ng-template #submissionCell let-row>
        <div class="leaderboard-cell leaderboard-cell--left">
          <span
            class="leaderboard-job-id"
            [attr.title]="row['job_id'] && row['job_id'] !== row['name'] ? row['job_id'] : null"
          >
            {{ row['name'] }}
          </span>
        </div>
      </ng-template>

      <ng-template #metricCell let-row let-column="column">
        <div class="leaderboard-cell leaderboard-cell--right">
          <span class="leaderboard-metric">{{ formatTableCellValue(column.key, row[column.key]) }}</span>
        </div>
      </ng-template>

      <ng-template #submittedCell let-row>
        <div class="leaderboard-cell leaderboard-cell--left">
          <span
            class="leaderboard-submitted"
            [attr.title]="formatSubmittedTitle(row['submitted_at'])"
          >
            {{ formatSubmittedValue(row['submitted_at']) }}
          </span>
        </div>
      </ng-template>

      <app-table
        class="leaderboard-table"
        [columns]="tableColumns"
        [data]="sortedTableRows"
        [loading]="isHackathonLoading"
        [skeletonRows]="8"
        [emptyMessage]="'No Results are announced yet'"
        [scrollable]="true"
        scrollHeight="560px"
        [striped]="true"
        [hoverable]="true"
        [bordered]="true"
        [initialSortColumn]="sortColumn"
        [initialSortDirection]="sortDirection"
        (sortChange)="onHackathonSortChange($event)"
      ></app-table>
    </div>
  </section>
</ng-container>

<!-- Providers leadership content (magazine design) -->
<ng-container *ngIf="boardVisualVariant !== 'hackathon' && selectedDesign === 'magazine'">
  <fieldset class="leadership-provider-fieldset leadership-category-providers">
    <legend class="leadership-provider-fieldset--legend">
      {{ headingLabel }}
    </legend>

    <div class="magazine-design" *ngIf="leadershipMembers.length > 0; else noResultsTemplate">
      <div class="magazine-featured animate-fade-in" *ngIf="topThree[0]">
        <div class="featured-main-grid">
          <div class="featured-col-9">
            <span class="featured-badge-top">#1 TOP LEADER</span>
            <div class="featured-name-section">
              <div class="featured-name-text">
                <div class="featured-name-row-inline">
                  <h2 class="featured-name">{{ topThree[0].name }}</h2>
                </div>
                <p class="featured-role">{{ topThree[0].role }}</p>
              </div>
            </div>
          </div>
          <div class="featured-col-3">
            <div
              class="featured-scores featured-scores--grid"
              *ngIf="topThree[0].totalPublished !== undefined || topThree[0].likes !== undefined || topThree[0].downloads !== undefined"
            >
              <div class="featured-score featured-score--inline">
                <span class="score-label">{{ primaryMetricLabel }}</span>
                <span class="score-metric">
                  <span class="score-value">{{ formatPrimaryMetricValue(topThree[0].totalPublished) }}</span>
                </span>
              </div>
              <div class="featured-score featured-score--inline" *ngIf="showEngagementMetrics">
                <span class="score-label">Upvotes</span>
                <span class="score-metric">
                  <span class="score-value">{{ topThree[0].likes ?? 0 }}</span>
                </span>
              </div>
              <div class="featured-score featured-score--inline" *ngIf="showEngagementMetrics">
                <span class="score-label">Downloads</span>
                <span class="score-metric">
                  <span class="score-value">{{ topThree[0].downloads ?? 0 }}</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="minimal-list-wrapper">
        <div class="minimal-list">
          <div class="minimal-item animate-fade-in" *ngFor="let member of leadershipMembers.slice(1)">
            <div class="minimal-details">
              <div class="minimal-name-row-inline">
                <h3 class="minimal-name">{{ member.name }}</h3>
                <div class="minimal-rank-badge minimal-rank-star" *ngIf="member.rank > 3">
                  <span class="star-rank-number">{{ member.rank }}</span>
                </div>
              </div>
              <p class="minimal-role">{{ member.role }}</p>
            </div>
            <div class="minimal-scores" *ngIf="member.totalPublished !== undefined || member.likes !== undefined || member.downloads !== undefined">
              <div class="minimal-score-card">
                <span class="score-label">{{ primaryMetricLabel }}</span>
                <span class="score-metric">
                  <span class="score-value">{{ formatPrimaryMetricValue(member.totalPublished) }}</span>
                </span>
              </div>
              <div class="minimal-score-card" *ngIf="showEngagementMetrics">
                <span class="score-label">Upvotes</span>
                <span class="score-metric">
                  <span class="score-value">{{ member.likes ?? 0 }}</span>
                </span>
              </div>
              <div class="minimal-score-card" *ngIf="showEngagementMetrics">
                <span class="score-label">Downloads</span>
                <span class="score-metric">
                  <span class="score-value">{{ member.downloads ?? 0 }}</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="load-more-container" *ngIf="showLoadMore && leadershipMembers.length >= 10">
        <button
          class="load-more-button"
          (click)="loadMoreProviders()"
          [disabled]="!providersPagination.hasNext || providersPagination.isLoading">
          <span *ngIf="!providersPagination.isLoading">Load More</span>
          <span *ngIf="providersPagination.isLoading">Loading more providers...</span>
        </button>
      </div>
    </div>
  </fieldset>
</ng-container>

<ng-template #noResultsTemplate>
  <div class="leaderboard-empty-state">
    <p class="leaderboard-empty-state__title">No results announced yet</p>
    <p class="leaderboard-empty-state__hint">Submissions will appear here once evaluation is complete.</p>
  </div>
</ng-template>
